<template>
    <div class="calculator">
        <div v-if="grouped_components.length">
            <b-row class="mt-2"
                   v-if="mode !== 3"
            >
                <b-col>
                    <b-alert show
                             variant="info"
                             dismissible
                             class="mb-2"
                    >
                        <div class="d-flex align-items-start">
                            <i class="fa fa-info-circle mr-2 mt-1"></i>
                            <div>
                                <strong>Información importante</strong>
                                <ol class="pl-3 mt-2 mb-0">
                                    <li>Si sabe que un componente no presenta toxicidad (ejemplo: agua) y es relevante
                                        considerar
                                        su porcentaje, marque la casilla en la columna correspondiente.
                                    </li>
                                    <li>Marcar estas casillas es necesario para que el sistema calcule correctamente los
                                        porcentajes totales.
                                    </li>
                                </ol>
                            </div>
                        </div>
                        <div class="d-flex align-items-start">
                            <i class="fa fa-warning mr-2 mt-2"/>
                            <div>
                                <p class="font-weight-bold mt-1 mb-0">
                                    Solo seleccione esta opción para sustancias que con certeza no presenta toxicidad en las vías de exposición indicadas.
                                </p>
                            </div>
                        </div>
                    </b-alert>
                </b-col>
            </b-row>

            <b-row>
                <b-col md="4"
                       class="my-3"
                >
                    <el-input id="name"
                              name="name"
                              v-model="name"
                              size="small"
                              type="text"
                              placeholder="Filtro por componente"
                              v-if="mode !== 3"
                    />
                    <i v-if="!!name.length"
                       @click="name = ''"
                       title="Cancelar, y restablecer datos."
                       class="fa fa-close filter-search-form-calc-icon-cancel cursor-pointer"
                    />
                </b-col>
                <b-col md="4"
                       class="my-3"
                >
                    <b-btn variant="info"
                           class="mx-1"
                           size="sm"
                           @click="downloadClassificationLongTermAquaticEnvironment"
                           v-if="mode === 2"
                    >
                        <i class="fa fa-file-pdf-o"/>
                        Descargar pdf
                    </b-btn>
                </b-col>
                <b-col md="4"
                       class="my-3"
                >
                    <b-button-group class="float-right mr-1"
                                    size="sm"
                    >
                        <b-button @click="mode = 1"
                                  :variant="mode === 1 ? 'secondary' : 'light'"
                        >
                            <i class="fa fa-list"/>
                        </b-button>
                        <b-button @click="mode = 2"
                                  :variant="mode === 2 ? 'secondary' : 'light'"
                        >
                            <i class="fa fa-table"/>
                        </b-button>
                        <b-button @click="mode = 3"
                                  :variant="mode === 3 ? 'info' : 'info-soft'"
                        >
                            <i class="fa fa-minus mr-1"/> SIN DATOS
                        </b-button>
                    </b-button-group>
                </b-col>
            </b-row>

            <form_mode v-if="mode === 1"
                       :name.sync="name"
                       @getClassification="_getClassification"
            />

            <summation_method v-else-if="mode === 2"
                              :name.sync="name"
                              @getClassification="_getClassification"
            />

            <div class="p-2 mb-2"
                 v-if="mode !== 3"
            >
                <b-row>
                    <b-col md="4"
                           class="mb-0"
                    >
                        <div class="metric-card p-3">
                            <div class="d-flex justify-content-between align-items-center mb-1">
                                <div class="metric-icon bg-soft-success">
                                    <svg xmlns="http://www.w3.org/2000/svg"
                                         width="32"
                                         height="32"
                                         viewBox="0 0 512 512"
                                         class="icon-success"
                                    >
                                        <path fill="currentColor"
                                              d="M335.45 256a214.83 214.83 0 0 1 29.08-108l7.62-13.26a280.7 280.7 0 0 0-48.64-7.15c-21.94-16.9-54.64-36.95-92.34-43.33L208 80l13.37 61.86c-41.67 14.14-78.43 42.86-102.76 62.62q-7.06-5.91-14.78-11.55c-39.71-29-82.6-31.8-84.4-31.9L0 160l2.67 19.31c.29 2 6.79 44.73 31.65 76.52C9.46 287.63 3 330.33 2.67 332.36L0 352l19.43-1.36c1.8-.1 44.69-2.89 84.4-31.9q7.58-5.53 14.56-11.37c24.37 19.83 61.14 48.6 102.86 62.74L208 432l23.17-4.22c37.49-6.34 70.08-26.4 92-43.32a284.3 284.3 0 0 0 49.32-7.23l-7.91-13.18A214.9 214.9 0 0 1 335.45 256"
                                        />
                                        <path fill="currentColor"
                                              d="M499.59 221.75c-5.85-9.88-16.54-24.9-34.19-40.28a209.8 209.8 0 0 0-62-37L392.23 164a183.22 183.22 0 0 0-.09 183.87l11.75 19.57a209.3 209.3 0 0 0 61.42-36.49C497.05 303.47 512 269 512 256c0-12.31-8-26.74-12.41-34.25M416 256a16 16 0 1 1 16-16a16 16 0 0 1-16 16"
                                        />
                                    </svg>
                                </div>
                                <div class="status-badge text-uppercase">CSEO PECES</div>
                            </div>
                            <h3 class="mb-1">
                                Total: {{ Math.round((totals.cseo_fish_total + Number.EPSILON) * 10000) / 10000 }} <span class="small text-muted">mg/l</span>
                            </h3>
                            <div v-if="mode === 2"
                                 class="d-flex justify-content-between align-items-end"
                            >
                                <div class="metric-detail">
                                    <div class="font-weight-bold small text-muted">Sumatoria:</div>
                                    <div class="text-muted">
                                        {{ Math.round((totals.cseo_fish + Number.EPSILON) * 10000) / 10000 }}
                                    </div>
                                </div>
                                <div class="percentage-badge badge-danger">
                                    {{ Math.round((totals.cseo_fish_percentage + Number.EPSILON) * 10000) / 10000 }} %
                                </div>
                            </div>
                        </div>
                    </b-col>

                    <b-col md="4"
                           class="mb-0"
                    >
                        <div class="metric-card p-3">
                            <div class="d-flex justify-content-between align-items-center mb-1">
                                <div class="metric-icon bg-soft-success">
                                    <svg xmlns="http://www.w3.org/2000/svg"
                                         width="48"
                                         height="48"
                                         viewBox="0 0 48 48"
                                         class="icon-success"
                                    >
                                        <path fill="currentColor"
                                              fill-rule="evenodd"
                                              d="M19.572 10.283a1 1 0 0 0-1.414.02l-3 3.086a1 1 0 0 0-.074 1.309c.744.962 1.463 1.674 2.374 2.175c.546.3 1.132.508 1.786.661c-.37.475-.718.992-1.043 1.54l-2.85-1.084l-1.182-2.756a1 1 0 1 0-1.838.789l1.35 3.145a1 1 0 0 0 .564.54l3.032 1.154l-.142.323q-.349.811-.633 1.67l-2.748-1.045l-1.922-2.88a1 1 0 1 0-1.664 1.111l2.1 3.145a1 1 0 0 0 .477.38l3.22 1.224q-.084.367-.153.736l-.101.04a12 12 0 0 0-1.873.964c-.513.327-1.072.75-1.513 1.267c-.439.515-.825 1.2-.825 2.026c0 1.204.438 2.53.823 3.48a18 18 0 0 0 .781 1.671l.015.028l.005.008l.001.003a1 1 0 0 0 1.75-.97l-.003-.004l-.01-.018l-.042-.079a16 16 0 0 1-.643-1.39c-.365-.9-.677-1.928-.677-2.73c0-.177.083-.417.347-.727c.262-.307.64-.607 1.066-.878a9 9 0 0 1 .612-.356q-.024.444-.025.882C15.5 35.156 19.622 39 24 39s8.5-3.844 8.5-10.257q0-.438-.025-.882c.197.105.406.224.612.356c.425.27.804.57 1.066.878c.264.31.347.55.347.728c0 .8-.312 1.828-.677 2.73a16 16 0 0 1-.685 1.468l-.01.018l-.002.004a1 1 0 1 0 1.749.97l.002-.003l.004-.008l.015-.028l.053-.099a18 18 0 0 0 .728-1.572c.385-.95.823-2.276.823-3.48c0-.825-.386-1.51-.825-2.026c-.44-.516-1-.94-1.512-1.267a12 12 0 0 0-1.975-1.004a20 20 0 0 0-.152-.736l3.22-1.225a1 1 0 0 0 .476-.379l2.1-3.145a1 1 0 0 0-1.663-1.11l-1.923 2.879l-2.748 1.045a21 21 0 0 0-.775-1.993l3.033-1.154a1 1 0 0 0 .563-.54l1.35-3.145a1 1 0 1 0-1.838-.789l-1.183 2.756l-2.85 1.084a15 15 0 0 0-1.041-1.54a7 7 0 0 0 1.785-.661c.911-.501 1.63-1.213 2.374-2.176a1 1 0 0 0-.074-1.308l-3-3.086a1 1 0 0 0-1.434 1.394l2.373 2.44c-.418.456-.796.76-1.202.983c-.57.313-1.271.512-2.365.662c-.096-.808-.38-1.556-.815-2.153c-.544-.746-1.38-1.315-2.399-1.315s-1.855.569-2.399 1.315c-.435.597-.718 1.345-.814 2.153c-1.094-.15-1.797-.35-2.366-.662c-.406-.223-.784-.527-1.201-.983l2.372-2.44a1 1 0 0 0-.02-1.414M34 34.529c.874.485.875.484.875.484zm-20 0l-.874.484zm9.217-19.722c-.27.37-.467.93-.467 1.593v.036l.006.521l-.424.303c-1.222.874-2.441 2.576-3.36 4.714c-.91 2.12-1.472 4.549-1.472 6.769C17.5 34.259 20.923 37 24 37s6.5-2.741 6.5-8.257c0-2.22-.562-4.65-1.472-6.769c-.919-2.138-2.138-3.84-3.36-4.714l-.424-.303l.006-.52V16.4c0-.663-.197-1.223-.467-1.593s-.56-.493-.783-.493c-.224 0-.513.123-.783.493"
                                              clip-rule="evenodd"
                                        />
                                    </svg>
                                </div>
                                <div class="status-badge text-uppercase">CSEO CRUSTÁCEOS</div>
                            </div>
                            <h3 class="mb-1">
                                Total: {{
                                    Math.round((totals.cseo_crustaceans_total + Number.EPSILON) * 10000) / 10000
                                }} <span class="small text-muted">mg/l</span>
                            </h3>
                            <div v-if="mode === 2"
                                 class="d-flex justify-content-between align-items-end"
                            >
                                <div class="metric-detail">
                                    <div class="font-weight-bold small text-muted">Sumatoria:</div>
                                    <div class="text-muted">
                                        {{ Math.round((totals.cseo_crustaceans + Number.EPSILON) * 10000) / 10000 }}
                                    </div>
                                </div>
                                <div class="percentage-badge badge-warning">
                                    {{
                                        Math.round((totals.cseo_crustaceans_percentage + Number.EPSILON) * 10000) / 10000
                                    }} %
                                </div>
                            </div>
                        </div>
                    </b-col>

                    <b-col md="4"
                           class="mb-0"
                    >
                        <div class="metric-card p-3">
                            <div class="d-flex justify-content-between align-items-center mb-1">
                                <div class="metric-icon bg-soft-success">
                                    <svg xmlns="http://www.w3.org/2000/svg"
                                         width="38"
                                         height="38"
                                         viewBox="0 0 512 512"
                                         class="icon-success"
                                    >
                                        <path fill="currentColor"
                                              d="M62 19c-22.537 0-41 18.463-41 41s18.463 41 41 41s41-18.463 41-41s-18.463-41-41-41m170.977 9.97c-16.978-.922-52.997 72.088-53.834 113.33c-.66 32.469 37.967 56.928 38.736 89.393c.857 36.173-28.546 58.95-38.397 101.532c-4.527 15.463 7.926 38.252 15.096 49.978c5.663 9.118 12.2 16.656 18.686 21.809c-6.163-28.894-5.196-59.292-.19-87.715c3.262-18.52 13.862-33.667 22.492-47.83s15.138-26.884 14.37-39.817c-.804-13.533-10.378-26.05-22.053-39.97s-25.352-29.016-28.285-49.514c-2.168-17.163 6.516-40.209 12.607-57.103l16.598 6.964c-6.53 15.961-11.005 30.288-11.387 47.59c1.947 13.606 12.458 26.428 24.258 40.496s24.992 29.612 26.23 50.469c1.15 19.365-8.029 35.584-16.966 50.252S233.15 307.076 230.8 320.42c-5.584 31.704-5.592 65.596 3.924 95.435c3.243 10.172 9.433 22.86 16.734 35.637a64 64 0 0 0 2.748-7.888c2.602-9.482 2.37-19.766-.57-24.102l14.898-10.104c8.174 12.053 6.356 26.848 3.03 38.97c-1.664 6.06-3.884 11.553-6.323 16.048c-.948 1.746-1.924 3.336-2.998 4.807a401 401 0 0 0 6.057 9.13A376 376 0 0 0 279.604 494h32.675c11.057-41.657-56.394-96.948-56.656-147.092c-.218-41.702 40.04-78.813 34.764-120.181c-4.188-32.838-41.059-53.739-50.657-85.42c-10.862-35.856 8.339-109.356-5.959-112.237a6 6 0 0 0-.794-.1M85 60c0 12.81-10.19 23-23 23S39 72.81 39 60c0-.124.008-.247.01-.371c2.796 2.023 6.204 3.236 9.865 3.236c9.282 0 17-7.718 17-17c0-3.219-.945-6.24 4.344-7.194C75.507 37.716 85 47.638 85 60m247.488 17.32c-18.119 0-33 14.882-33 33c0 18.12 14.881 33 33 33c18.12 0 33-14.88 33-33c0-18.118-14.88-33-33-33m5.315 18.96c5.7 2.11 9.685 7.532 9.685 14.04c0 8.391-6.609 15-15 15c-7.773 0-14.005-5.676-14.883-13.181c1.7.688 3.544 1.08 5.467 1.08c8.119 0 14.893-6.774 14.893-14.893c0-.695-.066-1.375-.162-2.047zm-37.315 45.04c-9.282 0-17 7.718-17 17s7.718 17 17 17s17-7.717 17-17c0-9.282-7.717-17-17-17m66.848 24.186a3.4 3.4 0 0 0-.596.039c-10.503 1.68 19.336 71.981 9.67 106.182c-5.636 19.943-32.977 29.52-37.213 49.808c-4.903 23.485 16.947 46.093 16.408 70.078c-.733 32.688-42.238 68.245-22.625 94.405c5.256 7.009 11.776-1.035 18.069-5.704a305 305 0 0 0 13.517-13.082a48 48 0 0 1-4.837-6.308a51 51 0 0 1-7.993-30.42l17.973.986c-.454 8.28 2.082 14.906 5.164 19.703c.595.927 1.21 1.753 1.82 2.522c1.635-2.015 3.157-3.972 4.41-5.776c6.183-8.891 11.334-18.161 11.418-25.935c.152-13.948-9.391-28.598-20.005-44.688c-10.614-16.089-22.44-34.122-19.098-55.761c2.02-13.086 11.901-20.663 19.61-26.848s13.486-11.354 14.833-16.244l17.354 4.781c-3.455 12.54-13.41 19.473-20.924 25.502s-12.346 10.775-13.084 15.557c-2.083 13.492 5.89 27.273 16.332 43.101c10.442 15.829 23.216 33.198 22.982 54.795c-.158 14.627-8.032 26.515-14.638 36.016c-8.105 11.656-20.935 24.708-32.735 35.424c-.136.124-.268.238-.404.361h34.926c9.11-3.612 17.256-8.636 23.16-14.686c20.682-21.19 11.655-57.69 4.309-88.726c-4.341-18.338-33.18-41.566-32.676-66.156c.405-19.792 28.159-32.332 28.457-52.125c.591-39.28-40.65-106.57-53.584-106.801m-288.252 5.137q-.304.015-.59.068c-10.503 1.94 12.938 49.915 9.668 74.863c-2.726 20.794-21.818 37.342-23.867 58.213C61.632 330.902 81.385 356.79 80 384c-1.724 33.881-50.665 71.441-31.268 97.729c4.765-4.382 10.554-10.11 16.575-16.625c13.477-14.583 27.891-33.125 33.732-46.319c5.181-11.703 6.3-26.25 4.834-40.08c-4.093-21.186-19.261-39.903-20.545-59.387c-1.076-20.698 9.154-37.472 11.904-52.1c2.151-11.44 5.143-36.394 5.143-36.394l17.875 2.117s-2.876 24.561-5.328 37.604c-3.574 19.007-12.318 34.359-11.617 47.838c3.083 21.238 18.184 39.242 20.468 58.424c1.699 16.013.694 33.525-6.273 49.263c-7.594 17.154-22.785 35.897-36.975 51.25a330 330 0 0 1-13.828 14.106c18.087 3.662 43.034-6.147 55.946-17.62c28.862-25.645 32.23-64.006 20.91-108.853c-7.237-28.668-22.264-48.927-20.48-74.678c1.085-15.683 27.202-25.725 18.622-43.33c-14.053-28.836-47.91-76.956-60.611-76.302M458.82 289.986c-7.618 0-14.66 2.639-20.267 7.035a15.3 15.3 0 0 0-5.24 5.118c-4.674 5.698-7.493 12.965-7.493 20.847c0 18.12 14.882 33 33 33c18.12 0 33-14.88 33-33c0-18.118-14.88-33-33-33m1.924 18.133c7.453.924 13.076 7.131 13.076 14.867c0 8.391-6.609 15-15 15c-7.843 0-14.127-5.776-14.914-13.382a14.5 14.5 0 0 0 2.063.164c8.118 0 14.892-6.774 14.892-14.893c0-.594-.046-1.178-.117-1.756M172.037 379.66c-3.674 46.106 5.923 85.524 35.01 114.34h50.219a432 432 0 0 1-3.82-5.482c-12.572-18.375-25.576-40.12-33.094-59.407c-17.026-4.533-30.563-19.501-41.065-36.412a160 160 0 0 1-7.25-13.039"
                                        />
                                    </svg>
                                </div>
                                <div class="status-badge text-uppercase">CSEO ALGAS</div>
                            </div>
                            <h3 class="mb-1">
                                Total: {{ Math.round((totals.cseo_algae_total + Number.EPSILON) * 10000) / 10000 }} <span class="small text-muted">mg/l</span>
                            </h3>
                            <div v-if="mode === 2"
                                 class="d-flex justify-content-between align-items-end"
                            >
                                <div class="metric-detail">
                                    <div class="font-weight-bold small text-muted">Sumatoria:</div>
                                    <div class="text-muted">
                                        {{ Math.round((totals.cseo_algae + Number.EPSILON) * 10000) / 10000 }}
                                    </div>
                                </div>
                                <div class="percentage-badge badge-info">
                                    {{
                                        Math.round((totals.cseo_algae_percentage + Number.EPSILON) * 10000) / 10000
                                    }}
                                    %
                                </div>
                            </div>
                        </div>
                    </b-col>
                </b-row>
            </div>

            <classification :classification.sync="classification"/>
        </div>
        <div v-else-if="mode !== 3">
            <b-card class="mt-2 mb-0"
                    body-class="p-3 text-center"
            >
                <h5 class="text-muted my-4 ">
                    <i class="fa fa-warning"/> No hay componentes para mostrar
                </h5>
            </b-card>
        </div>
    </div>
</template>

<script>

import {mapActions, mapState} from 'vuex';
import form_mode from './form_mode.vue';
import summation_method from './summation_method.vue';
import Classification from '../../../../components/classification.vue';

export default {
    name: 'index',
    components: {
        Classification,
        summation_method,
        form_mode
    },
    data() {
        return {
            classifications: [],
            tabs: 0,
            mode: 1,
            name: ''
        };
    },
    async mounted() {
        await this._getClassification();
    },
    computed: {
        ...mapState('calculator', [
            'product',
            'only_view',
            'totals',
            'classification',
            'grouped_components',
        ])
    },
    methods: {
        ...mapActions('calculator', [
            'getGroupedComponents',
            'getClassification',
            'downloadClassificationLongTermAquaticEnvironment'
        ]),
        async _getClassification(has_values = null) {
            await this.getGroupedComponents();

            let params = {
                product_id: this.product.id,
                danger_code: 'MCAL36',
                code_question_group: 'GQ14'
            };

            if (has_values !== null) {
                params.has_values = has_values;
            }

            await this.getClassification(params);

            if (has_values === null) {
                this.mode = this.classification.has_values == 0 ? 3 : 1;
            }
        }
    },
    watch: {
        async mode(new_value, old_value) {
            if (old_value === 3 || new_value === 3) {
                await this._getClassification(new_value);
            }
        }
    }
};
</script>

<style lang="scss"
       src="../../../../../../assets/calculator.scss"
/>

<style lang="scss">
.calculator {
    .metric-card {
        background-color: #fdfdfd;
        border-radius: 12px;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
        height: 100%;
        border: 1px solid #d9d9d9;
    }

    .metric-icon {
        width: 45px;
        height: 45px;
        border-radius: 12px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 20px;
    }

    .bg-soft-success {
        background-color: rgba(9, 123, 131, 0.15);
    }

    .status-badge {
        font-size: 14px;
        font-weight: bold;
        letter-spacing: 0.5px;
        color: #6c757d;
        background-color: #f1f1f1;
        padding: 4px 8px;
        border-radius: 4px;
    }

    .percentage-badge {
        font-size: 12px;
        font-weight: 600;
        padding: 4px 8px;
        border-radius: 4px;
        color: white;
        background-color: #1a94c0;
    }

    .badge-success {
        background-color: #1a94c0;
    }

    .icon-success {
        color: #1a94c0;
    }

    .component-card {
        background-color: white;
        border-radius: 12px;
        box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
        overflow: hidden;
    }

    .component-header {
        border-bottom: 1px solid rgba(0, 0, 0, 0.05);
    }

    .component-avatar {
        width: 48px;
    }

    .filter-search-form-calc-icon-cancel {
        position: absolute;
        right: 1.4rem;
        top: .45rem;
        color: #9c9c9c;
        font-size: 1.1rem;
    }

    .btn-info-soft {
        color: #ffffff;
        background-color: rgba(99, 194, 222, 0.49);
        border-color: rgba(121, 202, 226, 0.55);
    }
}
</style>
